// fabric import not used here
import { getState } from '~src/state';
import { FloatingControls } from './FloatingControls';
import { findProviderForObject } from './registry';
// Remove unused sidebar helpers

export class ControlsManager {
  constructor(canvas = null) {
    this.canvas = canvas || window.canvas;
    this.controls = new Map();
    this.activeControl = null;
    this.isPointerInside = true; // no longer used to hide/show on pointer leave

    this.boundHandleSelection = this.handleSelection.bind(this);
    this.boundHandleDeselection = this.handleDeselection.bind(this);
    this.boundHandleObjectRemoved = this.handleObjectRemoved.bind(this);
    this.boundCanvasMouseOver = this.handleCanvasMouseOver.bind(this);
    this.boundCanvasMouseOut = this.handleCanvasMouseOut.bind(this);
    this.boundCanvasMouseMove = this.handleCanvasMouseMove.bind(this);

    this.init();
  }

  init() {
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.canvas.on('selection:created', this.boundHandleSelection);
    this.canvas.on('selection:updated', this.boundHandleSelection);
    this.canvas.on('selection:cleared', this.boundHandleDeselection);
    this.canvas.on('object:removed', this.boundHandleObjectRemoved);

    const canvasEl = this.canvas?.upperCanvasEl;
    if (canvasEl) {
      canvasEl.addEventListener('pointerenter', this.boundCanvasMouseOver);
      canvasEl.addEventListener('pointerleave', this.boundCanvasMouseOut);
      canvasEl.addEventListener('pointermove', this.boundCanvasMouseMove);
    }
  }

  createControl(canvasObject, ControlClass = FloatingControls, options = {}) {
    if (this.controls.has(canvasObject)) {
      console.warn('Control already exists for this object');
      return this.controls.get(canvasObject);
    }

    const control = new ControlClass(canvasObject, options);
    control.hide();
    this.controls.set(canvasObject, control);

    return control;
  }

  createFloatingControls(canvasObject, options = {}) {
    return this.createControl(canvasObject, FloatingControls, options);
  }

  getControl(canvasObject) {
    return this.controls.get(canvasObject);
  }

  removeControl(canvasObject) {
    const control = this.controls.get(canvasObject);
    if (control) {
      control.destroy();
      this.controls.delete(canvasObject);

      if (this.activeControl === control) {
        this.activeControl = null;
      }
    }
  }

  showControl(canvasObject) {
    // Do not show controls while overlay workflows are active
    try {
      const { getState } = require('~src/state');
      const overlayActive =
        getState('isMagicEraserActive') ||
        getState('isExpandActive') ||
        getState('ischangeClothesActive');
      if (overlayActive) return;
    } catch (e) {
      console.error('Failed gating showControl due to overlay state', e);
    }

    const control = this.controls.get(canvasObject);
    if (control) {
      if (this.activeControl && this.activeControl !== control) {
        this.activeControl.hide();
      }
      // Always show the control on selection, regardless of pointer location
      control.show();
      this.activeControl = control;
    }
  }

  hideControl(canvasObject) {
    const control = this.controls.get(canvasObject);
    if (control) {
      control.hide();

      if (this.activeControl === control) {
        this.activeControl = null;
      }
    }
  }

  hideAllControls() {
    this.controls.forEach((control) => {
      control.hide();
    });
    this.activeControl = null;
  }

  handleSelection(e) {
    // Suppress floating controls while overlay features are active
    try {
      const overlayActive =
        getState('isMagicEraserActive') ||
        getState('isExpandActive') ||
        getState('ischangeClothesActive');
      if (overlayActive) return;
    } catch (e) {
      console.error('Failed gating selection handling due to overlay state', e);
    }
    // Always use the current active object from canvas to capture ActiveSelection/Group
    const raw =
      this.canvas && this.canvas.getActiveObject
        ? this.canvas.getActiveObject()
        : e && e.target
        ? e.target
        : e && e.selected
        ? e.selected[0]
        : null;
    if (!raw) return;

    // If a child of MagicFrame is selected, route to parent MagicFrame
    const selectedObject = raw._magicFrame ? raw._magicFrame : raw;

    let control = this.controls.get(selectedObject);
    if (!control) {
      // Create on demand via registered provider
      control = this.createControlsForObject(selectedObject);
    }
    if (control) this.showControl(selectedObject);
  }

  handleDeselection() {
    // Reset navigation state for all controls before hiding
    this.controls.forEach((control) => {
      if (control && typeof control.navigateTo === 'function') {
        try {
          control.navigateTo(control.options?.initialView || 'home');
        } catch (err) {
          void err;
        }
      }
    });
    this.hideAllControls();
  }

  handleCanvasMouseOver() {
    // Do not toggle visibility on pointer enter; keep controls persistent
    this.isPointerInside = true;
  }

  handleCanvasMouseOut(e) {
    const related = e?.relatedTarget;
    const relatedInsideCanvas =
      related &&
      this.canvas &&
      (related === this.canvas.upperCanvasEl ||
        related.closest?.('.floating-controls-container'));
    if (relatedInsideCanvas) return;
    // Do not hide controls when pointer leaves the canvas
    this.isPointerInside = false;
  }

  handleCanvasMouseMove(e) {
    const target = e?.target;
    if (!target) return;
    if (target.closest && target.closest('.floating-controls-container')) {
      this.isPointerInside = true;
    }
  }

  handleObjectRemoved(e) {
    this.removeControl(e.target);
  }

  attachControlToObject(
    canvasObject,
    ControlClass = FloatingControls,
    options = {}
  ) {
    const control = this.createControl(canvasObject, ControlClass, options);

    const originalSetCoords = canvasObject.setCoords;
    canvasObject.setCoords = function () {
      originalSetCoords.call(this);
      if (control) {
        control.updatePosition();
      }
    };

    return control;
  }

  createControlsForObject(object) {
    const provider = findProviderForObject(object);
    if (!provider) return null;
    try {
      const control = provider.create(this, object);
      if (control) {
        this.controls.set(object, control);
      }
      return control;
    } catch (e) {
      console.error('Failed to create control via provider', provider.id, e);
      return null;
    }
  }

  updateAllPositions() {
    this.controls.forEach((control) => {
      control.updatePosition();
    });
  }

  destroy() {
    this.controls.forEach((control) => {
      control.destroy();
    });
    this.controls.clear();

    this.canvas.off('selection:created', this.boundHandleSelection);
    this.canvas.off('selection:updated', this.boundHandleSelection);
    this.canvas.off('selection:cleared', this.boundHandleDeselection);
    this.canvas.off('object:removed', this.boundHandleObjectRemoved);

    const canvasEl = this.canvas?.upperCanvasEl;
    if (canvasEl) {
      canvasEl.removeEventListener('pointerenter', this.boundCanvasMouseOver);
      canvasEl.removeEventListener('pointerleave', this.boundCanvasMouseOut);
      canvasEl.removeEventListener('pointermove', this.boundCanvasMouseMove);
    }

    this.activeControl = null;
  }

  getControlsCount() {
    return this.controls.size;
  }

  hasControl(canvasObject) {
    return this.controls.has(canvasObject);
  }
}

let globalControlsManager = null;

export function getGlobalControlsManager() {
  if (!globalControlsManager && window.canvas) {
    globalControlsManager = new ControlsManager(window.canvas);
  }
  return globalControlsManager;
}

export function resetGlobalControlsManager() {
  if (globalControlsManager) {
    globalControlsManager.destroy();
    globalControlsManager = null;
  }
}
